import React, { useCallback } from 'react';

import type { DashboardScope, DashboardSection } from './dashboardSections';
import { SCOPE_LABELS } from './dashboardSections';

// Single source of truth for the sticky-header offset. The section anchors use
// `scroll-mt-80` (8rem); `window.scrollTo` reconciles against the same value so
// clicking a nav item and native anchor scrolling land identically.
const SCROLL_OFFSET_PX = 80;

interface DashboardSideNavProps {
  sections: DashboardSection[];
  activeSection: string;
  badges?: Record<string, number | undefined>;
}

interface SectionGroup {
  scope: DashboardScope | null;
  sections: DashboardSection[];
}

// Preserve section order while collapsing consecutive same-scope items into a
// labelled group. Sections without a scope (expert/client layouts) form a
// single unlabelled group.
function groupSections(sections: DashboardSection[]): SectionGroup[] {
  const groups: SectionGroup[] = [];
  for (const section of sections) {
    const scope = section.scope ?? null;
    const last = groups[groups.length - 1];
    if (last && last.scope === scope) {
      last.sections.push(section);
    } else {
      groups.push({ scope, sections: [section] });
    }
  }
  return groups;
}

export function DashboardSideNav({
  sections,
  activeSection,
  badges,
}: DashboardSideNavProps): React.ReactNode {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, section: DashboardSection) => {
      e.preventDefault();
      const scrollToTarget = () => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET_PX;
        window.scrollTo({ top, behavior: 'smooth' });
        // Move keyboard focus to the scrolled container so subsequent Tab order
        // starts from the new section. preventScroll stops the browser from
        // fighting the smooth scroll above.
        el.focus({ preventScroll: true });
      };
      // Defer one frame so the scroll runs after the click's paint.
      requestAnimationFrame(scrollToTarget);
    },
    []
  );

  const groups = groupSections(sections);

  return (
    <aside className="lg:mx-0 lg:px-0 lg:pt-0 sticky top-[52px] z-10 -mx-2 bg-surface-base px-2 pb-8 pt-12 lg:top-[9rem] lg:h-fit lg:w-[18rem] lg:shrink-0 lg:bg-transparent">
      <h2 className="mb-16 text-20 font-semibold">Dashboard</h2>
      <nav className="flex flex-row gap-16 overflow-x-auto pb-2 lg:flex-col lg:gap-20 lg:overflow-visible">
        {groups.map((group) => (
          <div
            key={group.scope ?? 'default'}
            className="flex shrink-0 flex-row gap-2 lg:shrink lg:flex-col"
          >
            {group.scope && (
              <h3 className="m-0 hidden text-[1.1rem] font-semibold uppercase tracking-wider text-on-surface-muted lg:mb-6 lg:block">
                {SCOPE_LABELS[group.scope]}
              </h3>
            )}
            <ul className="m-0 p-0 flex list-none flex-row gap-2 lg:flex-col">
              {group.sections.map((section) => {
                const isActive = activeSection === section.id;
                const badge = badges?.[section.id];

                return (
                  <li key={section.id} className="shrink-0 lg:w-full lg:shrink">
                    <a
                      href={`#${section.id}`}
                      onClick={(e) => handleClick(e, section)}
                      aria-current={isActive ? 'true' : undefined}
                      className={`flex items-center justify-between gap-8 whitespace-nowrap rounded-md px-12 py-10 text-14 no-underline transition-colors lg:py-8 lg:text-[1.3rem] ${
                        isActive
                          ? 'bg-brand-light font-semibold text-brand-light-ink'
                          : 'font-medium text-on-surface-secondary hover:bg-surface-elevated hover:text-on-surface'
                      }`}
                    >
                      <span>{section.label}</span>
                      {badge !== undefined && badge > 0 && (
                        <span className="inline-flex h-[1.8rem] min-w-[1.8rem] items-center justify-center rounded-full bg-yellow-500/15 px-6 text-10 font-semibold text-yellow-700 dark:text-yellow-300">
                          {badge}
                        </span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
